import { Link } from "react-router-dom";
import { motion } from "@/lib/motion-lite";
import { services } from "@/lib/services-data";
import {
  ArrowRight,
  PaintRoller,
  Home,
  Droplets,
  Hammer,
  Layers,
  Grid3x3,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";

const serviceIcons: Record<string, typeof Home> = {
  "interior-painting": PaintRoller,
  "exterior-painting": Home,
  waterproofing: Droplets,
  remodeling: Hammer,
  drywall: Layers,
  "tile-installation": Grid3x3,
  "residential-services": Wrench,
};

const ServicesSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="services" className="relative -mt-px section-padding section-gradient-soft cv-section">
      <div className="container-max">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            What We Do
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            Our <span className="text-gradient-primary">Services</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground max-w-2xl mx-auto text-lg"
          >
            Painting, waterproofing, remodeling, drywall, and tile — for homes and businesses across
            the Denver metro, the foothills, and Northern Colorado.
          </motion.p>
        </div>

        {/* 4-col track, each card spans 2 → two per row. A lone final card is centred. */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-7">
          {services.map((service, i) => {
            const Icon = serviceIcons[service.slug] ?? Home;
            const isLonelyLast = i === services.length - 1 && services.length % 2 === 1;

            return (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
                className={`w-full flex md:col-span-2 ${isLonelyLast ? "md:col-start-2" : ""}`}
              >
                {/* Entire card is one link — tappable on touch devices, no hover required */}
                <Link
                  to={`/services/${service.slug}`}
                  className="group relative rounded-2xl w-full flex flex-col card-hover-lift bg-card border border-border shadow-sm hover:shadow-2xl overflow-hidden isolate focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40"
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={service.image}
                      alt={`${service.title} by Diaz Brothers Construction`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 overlay-gradient" />

                    <div className="absolute top-4 left-4 z-10">
                      <div className="h-12 w-12 rounded-xl bg-primary/95 flex items-center justify-center backdrop-blur-sm shadow-lg">
                        <Icon className="h-6 w-6 text-primary-foreground" />
                      </div>
                    </div>

                    <div className="absolute top-4 right-4 z-10">
                      <span className="inline-block bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-md">
                        {service.price}
                      </span>
                    </div>

                    <div className="relative z-10 flex flex-col justify-end h-full p-6">
                      <h3 className="font-heading text-2xl text-white mb-0.5 tracking-tight font-bold drop-shadow-md">
                        {service.title}
                      </h3>
                      <p className="text-secondary text-sm font-semibold italic drop-shadow">
                        {service.titleEs}
                      </p>
                    </div>
                  </div>

                  <div className="mt-auto p-5 bg-card border-t border-border">
                    <p className="text-muted-foreground text-sm mb-3">{service.subtitle}</p>
                    <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                      View {service.title} details
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-3 mt-12">
          <Button size="lg" onClick={openQuoteForm} className="gap-2 rounded-xl font-semibold">
            Get Free Estimate <ArrowRight className="h-5 w-5" />
          </Button>
          <Button asChild size="lg" variant="outline" className="gap-2 rounded-xl font-semibold w-full">
            <Link to="/services">View All Services</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
